'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Text,
  Stack,
  Input,
  Button,
  HStack,
  Flex,
  TextProps,
  InputProps,
  BoxProps,
} from '@chakra-ui/react';
import { EditButton } from './EditButton';

interface InlineEditorProps {
  /**
   * Current value of the content being edited
   */
  value: string;

  /**
   * Function called when the content is saved
   */
  onSave: (value: string) => Promise<void> | void;

  /**
   * Text to display when no value is provided
   */
  placeholder?: string;

  /**
   * Whether the user can edit the content
   */
  canEdit?: boolean;

  /**
   * Props to pass to the text component
   */
  textProps?: TextProps;

  /**
   * Props to pass to the input component
   */
  inputProps?: InputProps;

  /**
   * Props to pass to the container
   */
  containerProps?: BoxProps;

  /**
   * Size of the save/cancel buttons
   */
  buttonSize?: string;

  /**
   * Whether to use optimistic updates (immediately show new value)
   */
  optimistic?: boolean;

  /**
   * Whether to allow saving empty values
   */
  allowEmpty?: boolean;
}

/**
 * A reusable component for inline editing of content
 */
export const InlineEditor = ({
  value,
  onSave,
  placeholder = 'No content',
  canEdit = true,
  textProps = {},
  inputProps = {},
  containerProps = {},
  buttonSize = 'md',
  optimistic = true, // Enable optimistic updates by default
  allowEmpty = false, // By default, don't allow empty values
}: InlineEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [displayValue, setDisplayValue] = useState(value); // For optimistic updates
  const inputRef = useRef<HTMLInputElement>(null);

  // Update the edit value when the value prop changes
  useEffect(() => {
    setEditValue(value);
    setDisplayValue(value);
  }, [value]);

  // Focus the input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!allowEmpty && editValue.trim() === '') return;

    // Store the trimmed value for consistency
    const trimmedValue = editValue.trim();

    // Apply optimistic update immediately
    if (optimistic) {
      setDisplayValue(trimmedValue);
    }

    setIsSaving(true);
    try {
      // Exit edit mode immediately with optimistic updates
      if (optimistic) {
        setIsEditing(false);
      }

      // Call the save function in the background
      await onSave(trimmedValue);

      // Only exit edit mode after save completes if not using optimistic updates
      if (!optimistic) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Failed to save content:', error);

      // Revert optimistic update on error
      if (optimistic) {
        setDisplayValue(value); // Revert to original value
        // Optionally re-enter edit mode to allow the user to try again
        // setIsEditing(true);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value); // Reset to original value
    setDisplayValue(value); // Ensure display value is also reset
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Flex width="100%" direction="column" {...containerProps}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          variant="flushed"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleCancel();
            }
          }}
          {...inputProps}
        />
        <HStack spacing="2" mt="2" mb="2">
          <Button
            colorScheme="brand"
            size={buttonSize}
            isLoading={isSaving}
            onClick={handleSave}
          >
            Spara
          </Button>
          <Button variant="ghost" size={buttonSize} onClick={handleCancel}>
            Avbryt
          </Button>
        </HStack>
      </Flex>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      width="100%"
      {...containerProps}
    >
      <Text {...textProps}>{displayValue || placeholder}</Text>
      {canEdit && (
        <EditButton onClick={() => setIsEditing(true)} size={buttonSize} />
      )}
    </Stack>
  );
};
